/**
 * Maps module/group identifiers from the navigation API to client-side
 * route paths. The API provides structure, ordering, and access control;
 * this registry provides the route mapping.
 *
 * When adding a new module in the backend, add a corresponding entry
 * here or it will be silently skipped in the sidebar.
 */

import type { OrganizationType } from '@/types/session';

// ============================================
// Identifier → Route Path (per org type)
// ============================================

const WORKPAY_ROUTES: Record<string, string> = {
  MODULE_HOME: '/w/home',
  MODULE_EMPLOYMENT_TYPES: '/w/employment-types',
  MODULE_FORMS: '/w/form',
  MODULE_TENANT_TEMPLATES: '/w/tenant-templates',
  MODULE_ENTITY_TEMPLATES: '/w/entity-templates',
  MODULE_EMPLOYEE_TEMPLATES: '/w/employee-templates',
  MODULE_PRODUCT_TYPES: '/w/product-types',
  MODULE_MODULE_REGISTRY: '/w/modules',
  MODULE_ROLE_MANAGEMENT: '/w/roles',
  MODULE_USER_MANAGEMENT: '/w/users',
  MODULE_TENANT_MANAGEMENT: '/w/clients',
  MODULE_ENTITY_MANAGEMENT: '/w/entities',
  MODULE_EMPLOYEES: '/w/employees',
  MODULE_CONTRACT_TEMPLATES: '/w/document-templates',
  MODULE_SIGNING_REQUESTS: '/w/signing-requests',
  MODULE_DOCUMENT_TYPES: '/w/document-types',
  MODULE_DOCUMENT_SNAPSHOTS: '/w/contracts',
  MODULE_ALL_DOCUMENTS: '/w/documents',
};

const TENANT_ROUTES: Record<string, string> = {
  MODULE_HOME: '/t/home',
  MODULE_ROLE_MANAGEMENT: '/t/roles',
  MODULE_USER_MANAGEMENT: '/t/users',
  MODULE_EMPLOYEES: '/t/employees',
  MODULE_TENANT_PROFILE: '/t/profile/self',
  MODULE_DOCUMENT_SNAPSHOTS: '/t/contracts',
  MODULE_ALL_DOCUMENTS: '/t/documents',
  MODULE_SIGNING_REQUESTS: '/t/signing-requests',
  MODULE_DEPARTMENTS: '/t/departments',
  MODULE_JOB_TITLES: '/t/job-titles',
};

export function getRouteForModule(
  identifier: string,
  orgType: OrganizationType,
): string | undefined {
  const routes = orgType === 'WORKPAY' ? WORKPAY_ROUTES : TENANT_ROUTES;
  return routes[identifier];
}

// ============================================
// Icon identifier → icon key
// ============================================

/**
 * Maps icon string from the API to an icon key used by the sidebar.
 * The actual SVG components are resolved in AppShell.
 */
export const ICON_MAP: Record<string, string> = {
  settings: 'configuration',
  people: 'people',
  briefcase: 'entities',
  building: 'org-profile',
  home: 'home',
};
